/**
 * Accessibility Statement Page
 *
 * Displays accessibility commitment and WCAG 2.2 AA compliance information.
 * BRUTALIST STREETWEAR DESIGN SYSTEM
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function Accessibility() {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Accessibility Statement | CPR Training Direct</title>
        <meta name="title" content="Accessibility Statement | CPR Training Direct" />
        <meta
          name="description"
          content="CPR Training Direct is committed to digital accessibility. Learn about our WCAG 2.2 AA compliance and accessibility features."
        />
        <meta name="author" content="CPR Training Direct" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cprtrainingdirect.com/accessibility" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cprtrainingdirect.com/accessibility" />
        <meta property="og:title" content="Accessibility Statement | CPR Training Direct" />
        <meta property="og:image" content="https://cprtrainingdirect.com/og-image.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Accessibility Statement | CPR Training Direct" />
      </Helmet>

      {/* Hero Section - Brutalist */}
      <section className="bg-indigo_dye-500 text-white py-16 md:py-20 px-6">
        <div className="container mx-auto text-center">
          <h1
            data-aos="fade-up"
            className="font-display text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-wider mb-4"
          >
            Accessibility
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="100"
            className="font-mono text-lg text-indigo_dye-200 uppercase tracking-wide"
          >
            Last Updated: October 30, 2024
          </p>
        </div>
      </section>

      {/* Page Container */}
      <div className="bg-white min-h-screen py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Back Link */}
            <div data-aos="fade-up" className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 font-mono text-indigo_dye-600 hover:text-amaranth_purple-600 font-bold uppercase tracking-wide transition-colors"
              >
                ← Back to Home
              </Link>
            </div>

            {/* Content */}
            <div data-aos="fade-up" data-aos-delay="100" className="border-4 border-gray-400 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)] p-8 md:p-12">
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-0 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Our Commitment
              </h2>
              <p className="font-body text-gray-700 mb-6 leading-relaxed">
                CPR Training Direct is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying WCAG 2.2 Level AA standards.
              </p>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Accessibility Features
              </h2>
              <ul className="list-none pl-0 mb-6 space-y-2">
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Alternative text for images and graphics</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Keyboard navigation support throughout</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Clear heading structure for screen readers</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Sufficient color contrast for readability</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Resizable text without loss of functionality</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Form labels and error messages for assistive tech</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> ARIA landmarks and labels for navigation</li>
              </ul>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Compatible Technologies
              </h2>
              <div className="bg-indigo_dye-50 border-4 border-indigo_dye-200 p-4 mb-6">
                <ul className="list-none pl-0 space-y-2">
                  <li className="font-body text-gray-700"><span className="font-mono font-bold">Screen Readers:</span> JAWS, NVDA, VoiceOver</li>
                  <li className="font-body text-gray-700"><span className="font-mono font-bold">Magnification:</span> Screen magnification software</li>
                  <li className="font-body text-gray-700"><span className="font-mono font-bold">Speech:</span> Speech recognition software</li>
                  <li className="font-body text-gray-700"><span className="font-mono font-bold">Input:</span> Alternative input devices</li>
                </ul>
              </div>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Physical Accessibility
              </h2>
              <p className="font-body text-gray-700 mb-4 leading-relaxed">
                Our training locations are designed to accommodate all students:
              </p>
              <ul className="list-none pl-0 mb-6 space-y-2">
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Wheelchair-accessible entrances</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Accessible parking spaces</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Accessible restrooms</li>
                <li className="flex items-start gap-2 font-body text-gray-700"><span className="text-amaranth_purple-500 font-bold">→</span> Adjustable seating and workstations</li>
              </ul>
              <p className="font-body text-gray-700 mb-6 leading-relaxed">
                Contact us at least 48 hours in advance for specific accommodation requests.
              </p>

              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mt-8 mb-4 border-b-4 border-indigo_dye-500 pb-2 inline-block">
                Contact Us
              </h2>
              <p className="font-body text-gray-700 mb-4 leading-relaxed">
                We welcome feedback on our accessibility. Contact our Accessibility Coordinator:
              </p>
              <div className="bg-amaranth_purple-50 border-4 border-amaranth_purple-200 p-4 mb-4">
                <p className="font-body text-gray-700">
                  <strong className="font-mono uppercase">CPR Training Direct</strong><br />
                  Contact us via our <Link to="/contact" className="text-indigo_dye-600 hover:text-amaranth_purple-600 font-semibold">contact page</Link>.<br />
                  <span className="font-mono text-sm">Response within 3 business days</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
